import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { apiClient } from "../api/client";
import { useUser } from "../auth/useUser";
import { useOnboardingState } from "../auth/useOnboardingState";
import { Header } from "../components/Header";
import { IdeaForm } from "../components/IdeaForm";
import { Onboarding } from "./Onboarding";
import { getDisplayName } from "../lib/displayName";
import { EditableTitle } from "../components/EditableTitle";
import { usePatchProductTitle } from "../auth/usePatchProductTitle";
import { useRunInFlightRefusal } from "../hooks/useRunInFlightRefusal";
import { useProducts } from "../hooks/useProducts";
import { dashboardTotal, lastActiveLine, rowLine, timeAgo } from "../lib/nextStep";
import type { components } from "../api/types";

type Product = components["schemas"]["ProductSummary"];

const EXAMPLES = [
  "AI-driven CRM for solo consultants",
  "Voice-first journaling app for runners",
  "Equipment-rental marketplace for film crews in EU",
];

export function Dashboard() {
  const { data: user } = useUser();
  const navigate = useNavigate();

  const productsQuery = useProducts();

  const products = productsQuery.data?.products ?? [];
  const isEmpty = !productsQuery.isLoading && products.length === 0 && !productsQuery.error;

  // Onboarding gate sits ABOVE the empty-state idea form: a brand-new
  // user with zero products sees the welcome flow first, and step 3
  // re-uses IdeaForm so the mental model stays consistent.
  const onboarding = useOnboardingState({
    user,
    productsCount: products.length,
    productsLoading: productsQuery.isLoading,
  });

  // 0016: every hook (here the "load more" query) is called UNCONDITIONALLY
  // before any early return; a hook below the isEmpty branch flipped the hook
  // count between renders (React error #300).
  // 0051 slice 2: the list is ordered by last activity, newest first. The
  // first page stays the shared ["products"] query; "load more →" loads the
  // pages after it here, under the products key, so every invalidation of the
  // list refreshes them too.
  const firstNext = productsQuery.data?.next_cursor ?? "";
  const [moreRequested, setMoreRequested] = useState(false);
  const more = useInfiniteQuery({
    queryKey: ["products", "more", firstNext],
    initialPageParam: firstNext,
    enabled: moreRequested && firstNext !== "",
    queryFn: async ({ pageParam }) => {
      const { data, error } = await apiClient.GET("/api/v1/products", {
        params: { query: { cursor: pageParam } },
      });
      if (error) throw error;
      return data;
    },
    getNextPageParam: (page) => page.next_cursor || undefined,
  });

  if (!user) return null;

  if (onboarding.shouldShow) {
    return <Onboarding user={user} onDismiss={onboarding.markComplete} />;
  }

  if (isEmpty) {
    // Zero-ideas state. Skip the products-list chrome entirely; this surface
    // is the activation moment, so it should feel like the app was waiting
    // for the founder to begin.
    return (
      <div className="min-h-screen flex flex-col">
        <Header user={user} />
        <main className="flex-1 flex items-start justify-center px-6 pt-24 pb-24">
          <div className="relative w-full max-w-2xl animate-slide-up">
            <div
              className="pointer-events-none absolute -top-32 left-1/2 h-[400px] w-[600px] -translate-x-1/2 rounded-full opacity-15 blur-3xl"
              style={{
                background: "radial-gradient(circle, rgba(124,122,255,0.6), transparent 70%)",
              }}
            />
            <div className="relative">
              <p className="font-mono text-xs text-ink-400 tracking-widest uppercase mb-3">
                Welcome, {user.display_name.split(" ")[0]}
              </p>
              <h1 className="text-display font-medium text-ink-50 tracking-tightest leading-[1.05]">
                What's your idea?
              </h1>
              <p className="mt-4 text-sm text-ink-300 leading-relaxed max-w-xl">
                Describe it in a sentence — the moat, who it's for, what it
                replaces. We'll turn it into a build-ready spec by surfacing
                the gaps and decisions you'll need to answer.
              </p>
              <div className="mt-10">
                <IdeaForm variant="page" examples={EXAMPLES} />
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // The first page, then every page loaded after it, each idea once.
  const rows: Product[] = [];
  const listed = new Set<string>();
  for (const p of [...products, ...(more.data?.pages ?? []).flatMap((page) => page.products)]) {
    if (listed.has(p.id)) continue;
    listed.add(p.id);
    rows.push(p);
  }
  const hasMore = firstNext !== "" && (more.data === undefined || more.hasNextPage);
  const loadingMore = more.isLoading || more.isFetchingNextPage;
  const loadMore = () => {
    if (!moreRequested) setMoreRequested(true);
    else if (more.data === undefined) void more.refetch();
    else void more.fetchNextPage();
  };

  // 0051 slice 2: the header's total is the server's, counted over every idea
  // (ideas_total, needs_you_total), never a count of the loaded rows; when the
  // server's count failed (either is absent) there is no total line.
  const ideasTotal = productsQuery.data?.ideas_total;
  const needsYouTotal = productsQuery.data?.needs_you_total;

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} />

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-8 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10 animate-fade-in">
          <div>
            <p className="font-mono text-xs text-ink-400 tracking-widest uppercase mb-2">
              {user.display_name}
            </p>
            <h1 className="text-display font-medium text-ink-50 tracking-tightest">Ideas</h1>
            {productsQuery.isLoading ? (
              <p className="mt-2 text-sm text-ink-300">loading…</p>
            ) : ideasTotal !== undefined && needsYouTotal !== undefined ? (
              <p data-testid="ideas-total" className="mt-2 text-sm text-ink-300">
                {dashboardTotal(ideasTotal, needsYouTotal)}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => navigate("/new")}
            className="group inline-flex items-center gap-2 rounded-md bg-accent px-3.5 py-2 text-sm font-medium text-ink-950 hover:bg-accent-glow transition-colors"
          >
            New idea
            <span className="opacity-50 group-hover:opacity-100 transition-opacity">+</span>
          </button>
        </div>

        {productsQuery.error && (
          <div className="border border-danger-solid/30 bg-danger-solid/10 rounded-md p-4 mb-6 text-sm text-danger-strong">
            Failed to load ideas. {String((productsQuery.error as Error).message ?? "")}
          </div>
        )}

        <div className="border-t border-ink-800">
          {rows.map((p, i) => (
            <ProductRow key={p.id} product={p} animationDelay={Math.min(i, 10) * 40} />
          ))}
        </div>

        {hasMore && (
          <div className="mt-10">
            <button
              type="button"
              onClick={loadMore}
              disabled={loadingMore}
              className="font-mono text-xs text-ink-400 hover:text-ink-200 transition-colors disabled:opacity-50"
            >
              {loadingMore ? "loading…" : "load more →"}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

function ProductRow({ product, animationDelay }: { product: Product; animationDelay: number }) {
  // 0028: inline-edit on the row itself. The ✎ icon is hidden
  // ONLY for products in the explicit "placeholder" state (fresh
  // products that haven't been analyzed yet — the founder must
  // wait for the first ANALYZE). Legacy products (pre-0028, empty
  // TitleSource) are grandfathered in: they're already past their
  // first analysis, so editing is allowed. EditableTitle's internal
  // stopPropagation keeps the wrapping <Link> from navigating when
  // the founder clicks ✎ or types in the input.
  const titleSource = product.title_source ?? "";
  const canEditTitle = titleSource !== "placeholder";
  const patchTitle = usePatchProductTitle(product.id);
  // 0159 F1: a stale row's rename refused under a live run lease reads F1's
  // line only until the server's answer about the run changes (the row's own
  // active_run_id is that answer).
  const titleRefusal = useRunInFlightRefusal(product.id, !!product.active_run_id);

  return (
    <Link
      to={`/products/${product.id}`}
      className="group block border-b border-ink-800 hover:bg-ink-900/40 transition-colors animate-slide-up"
      style={{ animationDelay: `${animationDelay}ms`, animationFillMode: "backwards" }}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 px-2 py-5">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-1">
            <h2 className="text-[15px] font-medium text-ink-100 group-hover:text-ink-50 transition-colors min-w-0 max-w-full">
              {/* 0159 F1: the pencil is hidden while a run is in flight
                  (the server refuses a rename under a live run lease). */}
              <EditableTitle
                title={getDisplayName(product)}
                canEdit={canEditTitle && !product.active_run_id}
                onSave={(next) => patchTitle.mutateAsync(next)}
                inFlightRefusal={titleRefusal}
              />
            </h2>
          </div>
          {product.title && product.idea && product.title !== product.idea && (
            <p className="text-[12px] text-ink-400 truncate mb-1">{product.idea}</p>
          )}
          <p className="font-mono text-[11px] text-ink-400">{lastActiveLine(timeAgo(product.last_active_at))}</p>
        </div>

        {/* 0051 slice 2: the idea's one next step, in the product page's own
            words (the server decides it; lib/nextStep words it). */}
        <p data-testid="row-next-step" className="text-[13px] text-ink-200 sm:max-w-[45%] sm:text-right">
          {rowLine(product.next_step)}
        </p>

        <span className="text-ink-500 group-hover:text-ink-200 transition-colors">→</span>
      </div>
    </Link>
  );
}
